import { useState, useEffect, useCallback } from 'react'
import {
  getRevenueSummary,
  getDailyRevenue,
  getTransactions,
  type RevenueSummaryItem,
  type DailyRevenueItem,
  type RevenueTransaction,
} from '../lib/api'
import { getDateRange } from '../lib/utils'

interface UseRevenueResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook for fetching revenue summary
 */
export function useRevenueSummary(params?: {
  revenue_type?: string
  chain?: string
}): UseRevenueResult<RevenueSummaryItem[]> {
  const [data, setData] = useState<RevenueSummaryItem[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getRevenueSummary(params)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch revenue summary')
    } finally {
      setLoading(false)
    }
  }, [params?.revenue_type, params?.chain])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Hook for fetching daily revenue
 */
export function useDailyRevenue(params: {
  start_date: string
  end_date: string
  revenue_type?: string
  chain?: string
}): UseRevenueResult<DailyRevenueItem[]> {
  const [data, setData] = useState<DailyRevenueItem[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getDailyRevenue(params)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch daily revenue')
    } finally {
      setLoading(false)
    }
  }, [params.start_date, params.end_date, params.revenue_type, params.chain])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Hook for fetching transactions with pagination
 */
export function useTransactions(params?: {
  limit?: number
  offset?: number
  revenue_type?: string
  chain?: string
}): UseRevenueResult<RevenueTransaction[]> {
  const [data, setData] = useState<RevenueTransaction[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getTransactions(params)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch transactions')
    } finally {
      setLoading(false)
    }
  }, [params?.limit, params?.offset, params?.revenue_type, params?.chain])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Hook for dashboard data with configurable date range
 */
export function useDashboardData(days: number = 30) {
  const { startDate, endDate } = getDateRange(days)

  const summary = useRevenueSummary()
  const dailyRevenue = useDailyRevenue({
    start_date: startDate,
    end_date: endDate,
  })

  const loading = summary.loading || dailyRevenue.loading
  const error = summary.error || dailyRevenue.error

  const refetch = useCallback(async () => {
    await Promise.all([summary.refetch(), dailyRevenue.refetch()])
  }, [summary.refetch, dailyRevenue.refetch])

  return {
    summary: summary.data,
    dailyRevenue: dailyRevenue.data,
    loading,
    error,
    refetch,
  }
}
